import React, { useState, useEffect, useCallback } from "react";
import { getToken } from '../utils/cookieUtils';
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { API_BASE_URL } from "../config";
import { authManager } from '../helpers/authManager';
import "./Reminder.css";

import LoggedNav from "../components/LoggedNav";
import { Box, Dialog, Typography } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { apiCall, getJsonResponse } from '../utils/apiUtils';

import reminder from "../images/icons/reminder.png";

const CHECKLIST_ITEMS = [
  {
    name: "inp1",
    label: "Please confirm that the Burners and oven if used are turned off",
  },
  {
    name: "inp2",
    label: "Please confirm that the dish washer is loaded",
  },
  {
    name: "inp3",
    label: "Please confirm the kitchen is clean",
  },
];

function Reminder(props) {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [bookerName, setBookerName] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [checkedItems, setCheckedItems] = useState({});
  const [validationError, setValidationError] = useState("");
  // New state for API message dialog
  const [showApiMessageDialog, setShowApiMessageDialog] = useState(false);
  const [apiMessage, setApiMessage] = useState("");
  // New state for payment failure dialog
  const [showPaymentFailureDialog, setShowPaymentFailureDialog] = useState(false);
  const [paymentFailureMessage, setPaymentFailureMessage] = useState("");
  // New state for 3DS verification dialog
  const [show3DSDialog, setShow3DSDialog] = useState(false);
  const [threeDSUrl, setThreeDSUrl] = useState("");
  const [threeDSMessage, setThreeDSMessage] = useState("");

  const saveMerchantIdToBackend = useCallback(async (merchantId, email) => {
    try {
      const token = getToken();
      const userId = localStorage.getItem("logged_user_id");

      if (!token || !userId) {
        window.location.href = "/chef/sign-in";
        return;
      }

      const res = await apiCall(`${API_BASE_URL}/user/add_chef_account`, {
        method: "POST",
        body: JSON.stringify({
          user_id: userId,
          merchantId,
          Email: email,
          merchantIdInPayPal: merchantId,
          permissionsGranted: true,
          accountStatus: "active",
          consentStatus: "active",
          isEmailConfirmed: "true",
          returnMessage: "Onboarding complete",
        }),
      });
      const data = await getJsonResponse(res);

      if (res.status === 401) {
        authManager.logout();
        window.location.href = "/chef/sign-in";
      } else if (data.status === true) {
        toast.success(data.message || "PayPal account linked successfully!");
      } else if (!res.ok || !data.success) {
        //console.error(data.message || "Failed to save Merchant ID.");
      }
    } catch (err) {
      //console.error("Save merchant error:", err);
    }
  }, []);

  useEffect(() => {
    document.title = "Reminder on Meal Prep Completion | CHEF | CHEF REPUBLIC";

    const savedBookingId = localStorage.getItem("reminder_bookingId");
    const savedBookerName = localStorage.getItem("reminder_bookerName");

    const finalBookingId = state?.bookingId || savedBookingId;
    const finalBookerName = state?.bookerName || savedBookerName;

    if (!finalBookingId || !finalBookerName) {
      navigate(-1);
      return;
    }

    setBookingId(finalBookingId);
    setBookerName(finalBookerName);
    localStorage.setItem("reminder_bookingId", finalBookingId);
    localStorage.setItem("reminder_bookerName", finalBookerName);
  }, [state, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const merchantId = params.get("merchantIdInPayPal");
    const email = params.get("email");

    if (merchantId) {
      saveMerchantIdToBackend(merchantId, email);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [saveMerchantIdToBackend]);

  const handleCheckboxChange = (name, checked) => {
    setCheckedItems((prev) => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    if (!recaptchaToken) {
      setValidationError("Please complete the reCAPTCHA");
      return false;
    }
    for (const item of CHECKLIST_ITEMS) {
      if (!checkedItems[item.name]) {
        setValidationError(`Please confirm: ${item.label}`);
        return false;
      }
    }
    setValidationError("");
    return true;
  };


  const proceedWithSubmission = async () => {
  setIsSubmitting(true);
  try {
    const res = await apiCall(
      `${process.env.REACT_APP_BASE_URL}status/chef_meal_preparation_complete`,
      {
        method: "POST",
        body: JSON.stringify({
          booking_id: bookingId.toString(),
          status: "6",
        }),
      }
    );
    const data = await getJsonResponse(res);

    // Success case
    if (data.success === true) {
      localStorage.removeItem("reminder_bookingId");
      localStorage.removeItem("reminder_bookerName");
      const successMsg = data.message || "Meal preparation completed successfully!";
      toast.success(successMsg);
      setTimeout(() => {
        window.location.href = "/chef/order-list";
      }, 1000);
      return;
    }

    // Payment failure case: show payment failure dialog
    if (data.success === false && data.payment_failed === true) {
      const message = data.message || "Kindly contact the Chef Republic admin for assistance.";
      setPaymentFailureMessage(message);
      setShowPaymentFailureDialog(true);
      return;
    }

    // 3DS required case: show 3DS verification dialog
    if (data.status === false && data.three_ds_required === true) {
      if (data.three_ds_url) {
        setThreeDSUrl(data.three_ds_url);
      }
      if (data.three_ds_message) {
        setThreeDSMessage(data.three_ds_message);
      }
      setShow3DSDialog(true);
      return;
    }

    // Failure case: show API message dialog and navigate to profile on OK
    if (data.status === false) {
      const message = data.message || "An error occurred. Please try again.";
      setApiMessage(message);
      setShowApiMessageDialog(true);
      return;
    }

    // Fallback for any other unexpected response
    setApiMessage("Kindly contact the Chef Republic admin for assistance.");
    setShowApiMessageDialog(true);
  } catch (err) {
    //console.error("Meal prep submission error:", err);
    setValidationError("Error while submitting. Please check your connection.");
  } finally {
    setIsSubmitting(false);
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    proceedWithSubmission();
  };

  const handleCloseMessageDialog = () => {
    setShowApiMessageDialog(false);
  };

  const handleOkAndNavigateToProfile = () => {
    setShowApiMessageDialog(false);
    navigate("/chef/profile");
  };

  const handleClosePaymentFailureDialog = () => {
    setShowPaymentFailureDialog(false);
  };

  const handleClose3DSDialog = () => {
    setShow3DSDialog(false);
  };

  const handleProceedTo3DS = () => {
    setShow3DSDialog(false);
  };

  return (
    <>
      <LoggedNav customer={props.customer} />

      <div
        className="container d-flex flex-column justify-content-center align-items-center"
        style={{ width: "95%", margin: "auto", height: "80vh" }}
      >
        <div
          className="my-5 p-3"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#ff5e41",
            borderRadius: "50%",
            height: "100px",
            width: "100px",
          }}
        >
          <img src={reminder} alt="reminder" style={{ width: "95%" }} />
        </div>

        <h1 className="fw-bold">Reminder</h1>

        <form onSubmit={handleSubmit}>
          <div className="d-flex flex-column">
            {CHECKLIST_ITEMS.map(({ name, label }) => (
              <div className="form-check m-3" key={name}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  name={name}
                  checked={!!checkedItems[name]}
                  onChange={(e) => handleCheckboxChange(name, e.target.checked)}
                />
                <label className="form-check-label">{label}</label>
              </div>
            ))}
          </div>

          {validationError && (
            <p className="text-danger text-center">{validationError}</p>
          )}

          <div className="mb-3 d-flex justify-content-center">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={setRecaptchaToken}
            />
          </div>

          <div className="d-flex justify-content-center">
            <button type="submit" className="btn-orange" disabled={isSubmitting || !recaptchaToken}>
              {isSubmitting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Confirm"
              )}
            </button>
          </div>
        </form>
        <p
          style={{
            fontSize: "0.7rem",
            color: "red",
            marginTop: "10px",
            lineHeight: "1.5",
            maxWidth: "650px"
          }}
        >
          I agree to accept and fulfill bookings only through Chef Republic for any clients introduced on the platform as outlined in the<a href="/chef/terms-and-conditions" target="_blank" rel="noopener noreferrer">
            Terms & Conditions
          </a>.
        </p>
      </div>

      
      {/* Payment Failure Dialog */}
      <Dialog
        open={showPaymentFailureDialog}
        onClose={handleClosePaymentFailureDialog}
        disableEscapeKeyDown
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Payment Failed
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {paymentFailureMessage}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            <a
              href="mailto:support@chefrepublic.us"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#FF5E41", textDecoration: "none" }}
            >
              Contact Us
            </a>
          </Typography>
          <button
            onClick={handleClosePaymentFailureDialog}
            className="btn btn-orange rounded-pill"
            style={{ minWidth: "120px" }}
          >
            Close
          </button>
        </Box>
      </Dialog>

      {/* 3DS Verification Dialog */}
      <Dialog
        open={show3DSDialog}
        onClose={handleClose3DSDialog}
        disableEscapeKeyDown
        PaperProps={{
          sx: { width: "450px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            3D Secure Verification Required
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {threeDSMessage}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <button
              onClick={handleProceedTo3DS}
              className="btn btn-orange rounded-pill"
              style={{ minWidth: "120px" }}
            >
              OK
            </button>
          </Box>
        </Box>
      </Dialog>

      {/* API Message Dialog - Navigates to Profile on OK */}
      <Dialog
        open={showApiMessageDialog}
        onClose={handleCloseMessageDialog}
        disableEscapeKeyDown
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Alert
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {apiMessage}
          </Typography>
          <button
            onClick={handleOkAndNavigateToProfile}
            className="btn btn-orange rounded-pill"
            style={{ minWidth: "120px" }}
          >
            Go To Profile
          </button>
        </Box>
      </Dialog>
    </>
  );
}

export default Reminder;